import React, { ReactNode, useCallback } from "react";

import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import { EntityTypeSupportingDataMerging } from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import { SourceObjectMessageArgs } from "@app/util/ui";

import styles from "./Footer.module.scss";

const messages = defineMessages({
  cancelButton: {
    id: "createModal.footer.cancel",
    defaultMessage: "Cancel",
    description: "Cancel button title on Create modal",
  },
  createAnywayButton: {
    id: "createModal.footer.okAnyway",
    defaultMessage:
      "Create {sourceObject, select, standard {{entityType, select, accounts {Company} contacts {Person} other {record}}} leadCompanies {Lead} siteCompanies {Site} projectCompanies {Project} shopCompanies {Shop} partnerPeople {Partner} leadPeople {Lead} staffMemberPeople {Staff Member} leadPersonnel {Lead Personnel} other {{label}}} anyway",
    description:
      'Create Anyway button title on Create modal. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
  },
  createButton: {
    id: "createModal.footer.ok",
    defaultMessage:
      "Create {sourceObject, select, standard {{entityType, select, accounts {Company} contacts {Person} other {record}}} leadCompanies {Lead} siteCompanies {Site} projectCompanies {Project} shopCompanies {Shop} partnerPeople {Partner} leadPeople {Lead} staffMemberPeople {Staff Member} leadPersonnel {Lead Personnel} other {{label}}}",
    description:
      'Create button title on Create modal. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
  },
  goBackButton: {
    id: "createModal.footer.back",
    defaultMessage: "Go Back",
    description: "Go Back button title on Create modal",
  },
  previewModeTooltip: {
    id: "createModal.footer.previewMode.tooltip",
    defaultMessage:
      "Please exit Preview Mode to create {sourceObject, select, standard {{entityType, select, accounts {company} contacts {person} other {record}}} leadCompanies {lead} siteCompanies {site} projectCompanies {project} shopCompanies {shop} partnerPeople {partner} leadPeople {lead} staffMemberPeople {staff member} leadPersonnel {lead personnel} other {{label}}}",
    description:
      'Create modal - OK button tooltip in preview mode. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
  },
});

export interface FooterProps {
  creating?: boolean;
  entityType: EntityTypeSupportingDataMerging;
  hasDuplicates?: boolean;
  isPreviewMode?: boolean;
  okDisabled?: boolean;
  okText?: ReactNode;
  onCancel?: () => void;
  onCreate?: (checkDuplicates: boolean) => void;
  onGoBack?: () => void;
  sourceObjectArgs: SourceObjectMessageArgs;
}

const Footer = ({
  creating,
  entityType,
  hasDuplicates,
  isPreviewMode,
  okDisabled,
  okText,
  onCancel,
  onCreate,
  onGoBack,
  sourceObjectArgs,
}: FooterProps) => {
  const intl = useIntl();

  const handleCreateClick = useCallback(() => {
    onCreate?.(!hasDuplicates);
  }, [onCreate, hasDuplicates]);

  return (
    <div className={styles.container}>
      {hasDuplicates && (
        <Button onClick={onGoBack} type="default">
          {intl.formatMessage(messages.goBackButton)}
        </Button>
      )}
      <div className={styles.rightButtons}>
        {hasDuplicates && (
          <Button onClick={onCancel} type="default">
            {intl.formatMessage(messages.cancelButton)}
          </Button>
        )}
        <Tooltip
          placement="topRight"
          title={
            isPreviewMode
              ? intl.formatMessage(messages.previewModeTooltip, { ...sourceObjectArgs, entityType })
              : null
          }
          trigger={isPreviewMode ? ["hover"] : []}
        >
          <Button
            disabled={okDisabled}
            loading={creating}
            onClick={handleCreateClick}
            type="primary"
          >
            {hasDuplicates
              ? intl.formatMessage(messages.createAnywayButton, { ...sourceObjectArgs, entityType })
              : (okText ??
                intl.formatMessage(messages.createButton, { ...sourceObjectArgs, entityType }))}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export default Footer;
